import React from 'react';
import { Button } from './ui/button';
import { Phone, MapPin, Navigation, Bike, UtensilsCrossed } from 'lucide-react';

const Order = () => {
  const deliveryPartners = [
    { name: 'Uber Eats', color: 'bg-green-600' },
    { name: 'Deliveroo', color: 'bg-cyan-600' },
    { name: 'Just Eat', color: 'bg-orange-600' }
  ];

  const handleCall = () => {
    window.location.href = 'tel:02078333399';
  };

  const handleDirections = () => {
    window.open('https://www.google.com/maps/place/6+Chapel+Market,+London+N1+9EZ', '_blank');
  };

  return (
    <section className="py-20 bg-zinc-900 relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl -translate-x-1/2" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-red-600/20 rounded-full blur-3xl translate-x-1/2" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Main CTA */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-600/20 border border-orange-500/50 rounded-full px-6 py-2 mb-6">
            <UtensilsCrossed className="h-5 w-5 text-orange-500" />
            <span className="text-orange-500 font-semibold">Ready to Order</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Hungry? <span className="text-orange-500">We're Ready.</span>
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Get your grill fix now - dine-in, takeaway or delivery
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
              onClick={handleCall}
            >
              <Phone className="mr-3 h-6 w-6" />
              Call Now - 020 7833 3399
            </Button>
            
            <Button 
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-xl font-bold transition-all duration-300 hover:scale-105"
              onClick={handleDirections}
            >
              <Navigation className="mr-3 h-6 w-6" />
              Get Directions
            </Button>
          </div>

          {/* Delivery Partners */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <span className="text-gray-400 font-semibold flex items-center gap-2">
              <Bike className="h-5 w-5" />
              Also available on:
            </span>
            {deliveryPartners.map((partner, index) => (
              <span 
                key={index}
                className={`${partner.color} text-white px-4 py-2 rounded-lg font-semibold text-sm hover:scale-105 transition-transform duration-300 cursor-pointer`}
              >
                {partner.name}
              </span>
            ))}
          </div>
        </div>

        {/* Location Card */}
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Map */}
          <div className="rounded-2xl overflow-hidden border-2 border-zinc-800 hover:border-orange-500 transition-colors duration-300 shadow-2xl h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.9087654321!2d-0.10578!3d51.53245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761b4e5e5e5e5e%3A0x5e5e5e5e5e5e5e5e!2s6%20Chapel%20Market%2C%20London%20N1%209EZ!5e0!3m2!1sen!2suk!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="N1 Grill Location"
            />
          </div>

          {/* Location Details */}
          <div className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl p-8 border border-zinc-700">
            <h3 className="text-3xl font-bold text-white mb-6">Visit Us</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Address</h4>
                  <p className="text-gray-300">6 Chapel Market</p>
                  <p className="text-gray-300">London N1 9EZ</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Phone</h4>
                  <a href="tel:02078333399" className="text-orange-500 hover:text-orange-400 text-lg font-semibold">
                    020 7833 3399
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UtensilsCrossed className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Opening Hours</h4>
                  <p className="text-gray-300">Open Daily</p>
                  <p className="text-gray-300">Closes at 23:30</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-700">
              <div className="flex flex-wrap gap-3">
                <span className="bg-zinc-700/50 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  Dine-in
                </span>
                <span className="bg-zinc-700/50 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  Takeaway
                </span>
                <span className="bg-zinc-700/50 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  Delivery
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Order;
